import React, { useState, useCallback, useEffect, useMemo } from 'react';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet';
import { Header } from './Header';
import { Footer } from './Footer';
import { GOOGLE_PLAY_APP_URL, APPLE_APP_STORE_URL } from "../utils/appStoreLinks";
import { pregnancyPageText as pregnancyPageText_en } from "../libs/en/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_en_ca } from "../libs/en-ca/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_de } from "../libs/de/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_de_ch } from "../libs/de-ch/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_fr } from "../libs/fr/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_fr_fr } from "../libs/fr-fr/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_fr_ca } from "../libs/fr-ca/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_nl } from "../libs/nl/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_it } from "../libs/it/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_it_ch } from "../libs/it-ch/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_cz } from "../libs/cz/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_fi } from "../libs/fi/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_pl } from "../libs/pl/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_dk } from "../libs/dk/texts-pregnancy-page-lib";
import { pregnancyPageText as pregnancyPageText_es } from "../libs/es/texts-pregnancy-page-lib";

function resolvePregnancyPageText(language) {
    switch (language) {
        case 'DE':
            return pregnancyPageText_de;
        case 'DE-CH':
            return pregnancyPageText_de_ch;
        case 'EN-CA':
            return pregnancyPageText_en_ca;
        case 'FR':
            return pregnancyPageText_fr;
        case 'FR-FR':
            return pregnancyPageText_fr_fr;
        case 'FR-CA':
            return pregnancyPageText_fr_ca;
        case 'NL':
            return pregnancyPageText_nl;
        case 'IT':
            return pregnancyPageText_it;
        case 'IT-CH':
            return pregnancyPageText_it_ch;
        case 'CZ':
            return pregnancyPageText_cz;
        case 'FI':
            return pregnancyPageText_fi;
        case 'PL':
            return pregnancyPageText_pl;
        case 'SE':
        case 'NO':
            return pregnancyPageText_en;
        case 'DK':
            return pregnancyPageText_dk;
        case 'ES':
            return pregnancyPageText_es;
        case 'PT':
            return pregnancyPageText_en;
        default:
            return pregnancyPageText_en;
    }
}

function resolvePrefix(language) {
    switch (language) {
        case 'DE':
            return '/de';
        case 'DE-CH':
            return '/de-ch';
        case 'EN-CA':
            return '/en-ca';
        case 'FR':
            return '/fr-ch';
        case 'FR-FR':
            return '/fr';
        case 'FR-CA':
            return '/fr-ca';
        case 'NL':
            return '/nl';
        case 'IT':
            return '/it';
        case 'IT-CH':
            return '/it-ch';
        case 'CZ':
            return '/cz';
        case 'FI':
            return '/fi';
        case 'PL':
            return '/pl';
        case 'SE':
            return '/se';
        case 'NO':
            return '/nb';
        case 'DK':
            return '/dk';
        case 'ES':
            return '/es';
        case 'PT':
            return '/pt';
        default:
            return '';
    }
}

function getHashId(href) {
    if (!href || !href.includes('#')) return '';
    return href.split('#').pop() || '';
}

function scrollToSection(id, behavior = 'smooth') {
    if (!id) return false;
    const el = document.getElementById(id);
    if (!el) return false;
    el.scrollIntoView({ behavior, block: 'start' });
    return true;
}

function getYouTubeVideoId(url) {
    if (!url) return null;
    const match = url.match(
        /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
    );
    return match ? match[1] : null;
}

function isYouTubeUrl(url) {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
}

function getYouTubeThumbnail(url) {
    const videoId = getYouTubeVideoId(url);
    // mqdefault is 16:9; hqdefault includes black letterbox bars on wide videos.
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
}

function getYouTubeWatchUrl(url) {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/watch?v=${videoId}` : url;
}

export function Pregnancy(props) {
    const pregnancyPageText = useMemo(
        () => resolvePregnancyPageText(props.language),
        [props.language]
    );
    const prefix = useMemo(
        () => resolvePrefix(props.language),
        [props.language]
    );
    const [isVideoOpen, setIsVideoOpen] = useState(false);

    const introVideo = pregnancyPageText?.introVideo;
    const introThumbnail = introVideo?.videoUrl ? getYouTubeThumbnail(introVideo.videoUrl) : '';
    const openVideo = useCallback(() => {
        setIsVideoOpen(true);
    }, []);

    const closeVideo = useCallback(() => {
        setIsVideoOpen(false);
    }, []);

    // Home CTA (and other in-app navigations) should land at the top of the page.
    // If a hash is present, smooth-scroll to that section after mount.
    useEffect(() => {
        const hashId = getHashId(window.location.hash);
        if (hashId) {
            // Ensure layout is ready before scrolling to the target section.
            const frame = window.requestAnimationFrame(() => {
                if (!scrollToSection(hashId, 'smooth')) {
                    window.scrollTo(0, 0);
                }
            });
            return () => window.cancelAnimationFrame(frame);
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        return undefined;
    }, []);

    // <base href="/"> rewrites bare "#id" links to "/#id" (home). Intercept and smooth-scroll instead.
    const handleAnchorClick = useCallback((e) => {
        const href = e.currentTarget.getAttribute('href') || '';
        const id = getHashId(href);
        if (!id) return;

        const el = document.getElementById(id);
        if (!el) return;

        e.preventDefault();
        scrollToSection(id, 'smooth');
        if (window.history && window.history.pushState) {
            window.history.pushState(null, '', `${window.location.pathname}#${id}`);
        }
    }, []);

    return (
        <div>
            <Helmet>
                <title>{parse(pregnancyPageText.title)}</title>
                <meta name="title" content={parse(pregnancyPageText.titleMeta)} />
                <meta name="description" content={parse(pregnancyPageText.descriptionMeta)} />
                <meta name="keywords" content={pregnancyPageText.keywords} />
                <meta charSet="utf-8" />
                <meta property="og:locale" content={pregnancyPageText.localeMeta} />
            </Helmet>
            <Header props={props} />

            <section className="inner-banner user-training-page pregnancy-pagebnr">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-sm-10">
                            <div className="user-training-header animations-page-header">
                                <h1>{pregnancyPageText?.pageTitle || 'Using CamAPS FX during pregnancy'}</h1>
                                <p className="page-subtitle">{pregnancyPageText?.pageSubtitle || 'Support at every stage'} </p>
                                <p className="page-subtitle-mobile">{pregnancyPageText?.pageSubtitleMobile || 'Support for type 1 diabetes before, during and after pregnancy'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="linknavigation" aria-label="Jump to page sections">
                <div className="container">
                    <p>{pregnancyPageText?.anchorNavLabel || 'Jump to:'}</p>
                    <ul className="link-list flex">
                        {pregnancyPageText?.ancernav?.map((navlink, index) => (
                            <li key={navlink.anchor || navlink.url || index}>
                                <a
                                    href={`${prefix}/pregnancy/#${navlink.anchor || getHashId(navlink.url || '')}`}
                                    onClick={handleAnchorClick}
                                    aria-label={navlink.name}
                                    title={navlink.name}
                                >
                                    {navlink.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Intro */}
            <section className="pregnancy-intro">
                <div className="container">
                    <div>
                        <div>
                            <h2>{pregnancyPageText?.introHeading || 'T1D during pregnancy can be challenging.'}</h2>
                            <div dangerouslySetInnerHTML={{ __html: pregnancyPageText?.introBody || '' }} className="intro-body" />
                        </div>
                        {introVideo && (
                            <button
                                type="button"
                                onClick={openVideo}
                                aria-label={pregnancyPageText?.introVideoPlayLabel || 'Play video: T1D during pregnancy'}
                            >
                                <img
                                    src={introThumbnail}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <span className="animations-play-button-overlay play-button" aria-hidden="true">
                                    <svg width="19" height="24" viewBox="0 0 19 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18 12L1 23V1L18 12Z" fill="#04ABA0" stroke="#04ABA0" strokeWidth="2" />
                                    </svg>
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {isVideoOpen && introVideo && (
                <div
                    className="video-modal-overlay"
                    onClick={closeVideo}
                    role="presentation"
                >
                    <div
                        className="video-modal-container"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label={introVideo?.title || 'Video'}
                    >
                        <button
                            type="button"
                            onClick={closeVideo}
                            className="video-modal-close"
                        >
                            <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L17 17M17 1L1 17" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <span>{parse(pregnancyPageText?.videoCloseButtonText || 'Close')}</span>
                        </button>
                        <div className="video-modal-video-wrapper">
                            {isYouTubeUrl(introVideo.videoUrl) ? (
                                <iframe
                                    className="video-modal-video"
                                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(introVideo.videoUrl)}?autoplay=1&mute=0&rel=0&modestbranding=1&enablejsapi=1`}
                                    title={introVideo.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    referrerPolicy="strict-origin-when-cross-origin"
                                />
                            ) : (
                                <video controls autoPlay className="video-modal-video">
                                    <source src={introVideo.videoUrl} type="video/mp4" />
                                    {parse(pregnancyPageText?.videoNotSupportedText || 'Your browser does not support the video tag.')}
                                </video>
                            )}
                        </div>
                        {isYouTubeUrl(introVideo.videoUrl) && (
                            <p className="video-modal-youtube-fallback">
                                <a
                                    href={getYouTubeWatchUrl(introVideo.videoUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {parse(pregnancyPageText?.watchOnYouTubeText || 'If the video does not play, watch on YouTube')}
                                </a>
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Callout — planning */}
            <section className="pregnancy-callout pregnancy-callout--planning" aria-label="Pregnancy planning callout">
                <div className="container">
                    <div>
                        <p>{pregnancyPageText?.calloutPlanning || 'Pregnancy requires much tighter diabetes management to protect the foetus from high glucose exposure.'}</p>
                    </div>
                </div>
            </section>

            {/* Planning */}
            <section className="pregnancy-stage" id="pregnancy-planning">
                <div className="container">
                    <div>
                        <h2>{pregnancyPageText?.planningTitle || 'Planning your pregnancy'}</h2>
                        <p>
                            <strong>{pregnancyPageText?.planningLead || 'Planning ahead of your pregnancy is essential for the best outcomes for you and your baby.'}</strong>
                        </p>
                        <div dangerouslySetInnerHTML={{ __html: pregnancyPageText?.planningBody || '' }} className="planning-body" />
                        <div className="stat-band is-target">
                            <p>{pregnancyPageText?.planningStatLabel || 'Pre-pregnancy HbA1c target:'}</p>
                            <p>{pregnancyPageText?.planningStatValue || 'under 6.5%'}</p>
                            <p>{pregnancyPageText?.planningStatUnit || '(48 mmol/mol)'}</p>
                        </div>
                        <p>{pregnancyPageText?.planningFooter || 'Before conception, women with type 1 diabetes are generally advised to aim for an HbA1c below 6.5% (48 mmol/mol), where this can be achieved safely. Your team may also help you optimise your myLoop settings and support you in getting as close to target as possible.'}</p>
                    </div>
                </div>
            </section>

            {/* HCL highlight */}
            <section className="pregnancy-highlight" aria-label="Hybrid closed-loop therapy highlight">
                <div className="container">
                    <div>
                        <h3>{pregnancyPageText?.hclCardTitle || 'Hybrid closed-loop therapy is changing pregnancy care'}</h3>
                        <p>{parse(pregnancyPageText?.hclCardText || 'In the 2025 NPID HCL analysis, 40.5% of women were offered HCL and, of those offered it, 76.8% accepted.<sup>2</sup>')}</p>
                    </div>
                </div>
            </section>

            {/* Callout — first trimester */}
            <section className="pregnancy-callout pregnancy-callout--first-trimester" aria-label="First trimester callout">
                <div className="container">
                    <div>
                        <p>{pregnancyPageText?.calloutFirstTrimester || 'Insulin needs can be more unpredictable in early pregnancy, so regular contact with your healthcare team is important.'}</p>
                    </div>
                </div>
            </section>

            {/* First trimester */}
            <section className="pregnancy-stage pregnancy-stage--during" id="during-pregnancy">
                <div className="container">
                    <div>
                        <h2>{pregnancyPageText?.firstTrimesterTitle || 'First trimester'}</h2>
                        <p>
                            <strong>{pregnancyPageText?.firstTrimesterLead || 'The first trimester can be a time of greater glucose variability, and some people may be more prone to hypos as insulin sensitivity changes.'}</strong>
                        </p>
                        <p>{parse(pregnancyPageText?.firstTrimesterBody || '')}</p>
                        <div className="stat-band is-percent">
                            <p>{pregnancyPageText?.firstTrimesterStatValue || '+70%'}</p>
                            <p>{pregnancyPageText?.firstTrimesterStatLabel || 'Time between 3.5–7.8 mmol/L (63–140mg/dL)'}</p>
                        </div>
                        <p>{parse(pregnancyPageText?.firstTrimesterFooter || 'For CamAPS FX users, your healthcare team may consider a <strong>Personal Glucose Target</strong> of around 5.5 mmol/L (99 mg/dL) in the first trimester, if clinically appropriate.')}</p>
                    </div>
                </div>
            </section>

            {/* Callout — second and third trimester */}
            <section className="pregnancy-callout pregnancy-callout--second-trimester" aria-label="Second and third trimester callout">
                <div className="container">
                    <div>
                        <p>{pregnancyPageText?.calloutSecondThirdTrimester || 'From around 16 weeks, insulin resistance typically rises, meaning settings may need to be reviewed more often.'}</p>
                    </div>
                </div>
            </section>

            {/* Second and third trimester */}
            <section className="pregnancy-stage pregnancy-stage--during" id="second-third-trimester">
                <div className="container">
                    <div>
                        <h2>{pregnancyPageText?.laterTrimesterTitle || 'Second and third trimester'}</h2>
                        <p>
                            <strong>{pregnancyPageText?.laterTrimesterLead || 'As pregnancy progresses, insulin resistance usually increases and insulin needs often rise, especially from around 16 weeks onwards.'}</strong>
                        </p>
                        <p>{parse(pregnancyPageText?.laterTrimesterBody || '')}</p>
                        <div className="stat-dual">
                            <div className="stat-band is-pgt">
                                <p>{pregnancyPageText?.laterTrimesterDayStatLabel || 'Daytime PGT of'}</p>
                                <p>{pregnancyPageText?.laterTrimesterDayStatValue || '5.0'}</p>
                                <p>{pregnancyPageText?.laterTrimesterDayStatUnit || 'mmol/L (90mg/dL)'}</p>
                            </div>
                            <div aria-hidden="true" />
                            <div className="stat-band is-pgt">
                                <p>{pregnancyPageText?.laterTrimesterNightStatLabel || 'Overnight PGT of'}</p>
                                <p>{pregnancyPageText?.laterTrimesterNightStatValue || '4.5'}</p>
                                <p>{pregnancyPageText?.laterTrimesterNightStatUnit || 'mmol/L (81mg/dL)'}</p>
                            </div>
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: pregnancyPageText?.laterTrimesterFooter || '' }} className="later-trimester-footer" />
                    </div>
                </div>
            </section>

            {/* Callout — NPID */}
            <section className="pregnancy-callout pregnancy-callout--npid" aria-label="NPID analysis callout">
                <div className="container">
                    <div>
                        <p>{parse(pregnancyPageText?.calloutLabour || 'The 2025 NPID HCL analysis showed encouraging real-world patterns for CamAPS FX in type 1 diabetes pregnancy care.<sup>2</sup>')}</p>
                    </div>
                </div>
            </section>

            {/* Labour and birth */}
            <section className="pregnancy-labour" id="labour-and-birth">
                <div className="container">
                    <div>
                        <div>
                            <h2>{pregnancyPageText?.labourTitle || 'Labour and birth'}</h2>
                            <p>
                                <strong>{pregnancyPageText?.labourIntro || 'Your diabetes and obstetric teams can help agree how insulin delivery will be managed during labour or caesarean section.'}</strong>
                            </p>
                            <p>
                                {pregnancyPageText?.labourBody || 'In some cases, CamAPS FX can be continued during labour or caesarean section if agreed with the healthcare team and if the mother feels well enough and confident to self-manage.'}
                            </p>
                        </div>
                        <div className="stat-band is-labour">
                            <p>{pregnancyPageText?.labourStatTitle || 'Every birth plan is individual'}</p>
                            <p>{pregnancyPageText?.labourStatText || 'Diabetes management during delivery should always be agreed with the obstetric and diabetes teams.'}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* After the pregnancy */}
            <section className="pregnancy-stage pregnancy-stage--postnatal" id="post-natal">
                <div className="container">
                    <div>
                        <h2>{pregnancyPageText?.postNatalTitle || 'After the pregnancy'}</h2>
                        <p>
                            <strong>{pregnancyPageText?.postNatalLead || 'After delivery, insulin requirements can drop quickly, so settings should be reviewed as soon as possible to reduce the risk of hypoglycaemia.'}</strong>
                        </p>
                        <div dangerouslySetInnerHTML={{ __html: pregnancyPageText?.postNatalBody || '' }} className="post-natal-body" />
                    </div>
                </div>
            </section>

            {/* Designed to support */}
            <section className="pregnancy-highlight pregnancy-highlight--designed" aria-label="Designed to support changing needs">
                <div className="container">
                    <div>
                        <h3>{pregnancyPageText?.designedCardTitle || 'Designed to support changing needs'}</h3>
                        <p>{pregnancyPageText?.designedCardText || 'CamAPS FX adjusts insulin delivery every 8–12 minutes, based on current and predicted glucose levels.'}</p>
                    </div>
                </div>
            </section>

            {/* References */}
            <section className="pregnancy-references" aria-label="References">
                <div className="container">
                    <div dangerouslySetInnerHTML={{ __html: pregnancyPageText?.references || '' }} className="references-body" />
                </div>
            </section>

            {/* Getting Started Section */}
            <section className="getting-started-section">
                <div className="container-lg">
                    <div className="row">
                        {/* Left Column */}
                        <div className="col-12 col-lg-6">
                            {/* Title */}
                            <h2>{pregnancyPageText.gettingStartedHeader || 'Ready to get started?'}</h2>

                            {/* Step 1 */}
                            <div className="getting-started-step">
                                <h3 className="step-title">
                                    <p>
                                        <span dangerouslySetInnerHTML={{ __html: pregnancyPageText?.step1Title || '<strong>Step 1:</strong> Download the app' }} />
                                    </p>
                                </h3>
                                <div className="logo-container">
                                    <a href={GOOGLE_PLAY_APP_URL}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        <img src="/images/google-play-badge.png"
                                            alt={pregnancyPageText?.altGooglePlay || 'Get it on Google Play'} />
                                    </a>
                                    <a href={APPLE_APP_STORE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        <img src="/images/app-store.png"
                                            alt={pregnancyPageText?.altAppStore || 'Download on App Store'} />
                                    </a>
                                </div>
                            </div>

                            {/* Step 2 */}
                            <div className="getting-started-step step-2">
                                <h3>
                                    <p>
                                        <span dangerouslySetInnerHTML={{ __html: pregnancyPageText?.step2Title || '<strong>Step 2:</strong> Create an account' }} />
                                    </p>
                                </h3>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="col-12 col-lg-6">
                            {/* Step 3 */}
                            <div className="getting-started-step step-3">
                                <h3>
                                    <p>
                                        <span dangerouslySetInnerHTML={{ __html: pregnancyPageText?.step3Title || '<strong>Step 3:</strong> Begin the in-app essential training' }} />
                                    </p>
                                </h3>
                            </div>

                            {/* Step 4 */}
                            <div className="getting-started-step step-4">
                                <h3>
                                    <p>
                                        <span dangerouslySetInnerHTML={{ __html: pregnancyPageText?.step4Title || '<strong>Step 4:</strong> Pair with your chosen pump and CGM (with the approval of your healthcare team) or explore the app with <strong>Virtual Mode</strong>' }} />
                                        <span className="tooltip-wrapper">
                                            <span className="tooltip-container">
                                                <img
                                                    src="/images/info-circle.svg"
                                                    alt={pregnancyPageText?.altInfo || 'Info'}
                                                    className="tooltip-icon"
                                                />
                                                <span className="tooltip-text">
                                                    {pregnancyPageText?.virtualModeTooltipText || 'Virtual mode allows you to trial the app without being connected to a real insulin pump and CGM sensor.'}
                                                </span>
                                            </span>
                                        </span>
                                    </p>
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer props={props} />
        </div >
    );
}
